import { useState } from 'react';
import type { Avatar, StickerRef } from '@kinop/shared';
import { cn } from '@/lib/utils';
import { StickerImage } from './StickerPicker.js';

interface UserAvatarProps {
  nickname: string;
  avatar: Avatar | null;
  className?: string;
}

/**
 * Цвет из имени — запасной вариант для тех, кто ничего не выбирал.
 *
 * Важно, что он не случайный: у одного человека он всегда один и тот же,
 * и в списке участников его узнают по цвету, а не только по подписи.
 */
function colorForName(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) % 360;
  }
  return `hsl(${hash} 65% 62%)`;
}

function initial(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '?';
  return [...trimmed][0]!.toUpperCase();
}

/**
 * Значок рядом с именем — премиум-эмодзи из телеграма.
 *
 * Двигается, как и в телеграме: рисованные значки на то и рисованные.
 * Тяжести в этом нет — проигрыватель грузится один на всю страницу и
 * работает только у тех значков, что сейчас на экране.
 */
export function UserBadge({ badge }: { badge: StickerRef | null | undefined }): JSX.Element | null {
  if (!badge) return null;
  return <StickerImage sticker={badge} className="badge-emoji" />;
}

export function UserAvatar({ nickname, avatar, className }: UserAvatarProps): JSX.Element {
  // Картинка может и не загрузиться: фотографию в телеграме меняют, а файл
  // мы кэшируем. Тогда молча возвращаемся к мордочке, а не к пустому месту.
  const [photoBroken, setPhotoBroken] = useState(false);
  const photo = photoBroken ? null : avatar?.photoUrl;

  return (
    <span
      className={cn('avatar', className)}
      style={{ background: avatar?.color ?? colorForName(nickname) }}
      aria-hidden="true"
    >
      {photo ? (
        <img className="avatar__photo" src={photo} alt="" onError={() => setPhotoBroken(true)} />
      ) : (
        (!avatar?.face || avatar.face === '?' ? initial(nickname) : avatar.face)
      )}
    </span>
  );
}
